// Zombie Merge — DRAG-TO-MERGE engine. Tiles stay where they are; you
// physically pick one up and drop it onto another. No whole-board slide.
//
// State:
//   tiles:     [{ id, row, col, value, isNew?, justMerged? }]
//   score:     number
//   best:      number
//   moves:     number
//   nextId:    number
//   won:       boolean      (true once 2048 reached the first time)
//   keepGoing: boolean
//   startTime: number

const SIZE = 4;            // 4x4 grid
const WIN_VALUE = 2048;
const SPAWN_FOUR_CHANCE = 0.15;
const STARTING_TILES = 4;  // Initial fill so the player has stuff to work with

export { SIZE, WIN_VALUE };

export function newGame() {
  const state = {
    tiles: [],
    score: 0,
    best: 0,
    moves: 0,
    nextId: 1,
    won: false,
    keepGoing: false,
    startTime: Date.now(),
  };
  for (let i = 0; i < STARTING_TILES; i++) spawnTile(state);
  return state;
}

function clone(state) {
  return { ...state, tiles: state.tiles.map((t) => ({ ...t })) };
}

function gridOf(tiles) {
  const grid = Array.from({ length: SIZE }, () => Array(SIZE).fill(null));
  for (const t of tiles) grid[t.row][t.col] = t;
  return grid;
}

// Picks a random empty cell. Returns null if board is full.
function pickEmpty(state) {
  const grid = gridOf(state.tiles);
  const empty = [];
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++) if (!grid[r][c]) empty.push([r, c]);
  if (!empty.length) return null;
  return empty[Math.floor(Math.random() * empty.length)];
}

function spawnTile(state) {
  const cell = pickEmpty(state);
  if (!cell) return false;
  const [r, c] = cell;
  const value = Math.random() < SPAWN_FOUR_CHANCE ? 4 : 2;
  state.tiles.push({
    id: state.nextId++,
    row: r,
    col: c,
    value,
    isNew: true,
  });
  return true;
}

function clearFlags(state) {
  for (const t of state.tiles) {
    delete t.isNew;
    delete t.justMerged;
  }
}

// Attempt to move tile `tileId` to (destRow, destCol). Returns:
//   { state, result: 'merge' | 'move' | 'invalid', mergedValue?, scoreGained? }
// - 'merge': destination has a same-value tile; src is removed, dest doubles.
// - 'move':  destination is empty; src relocates there.
// - 'invalid': different-value tile in destination, or same cell.
export function tryDragMove(prev, tileId, destRow, destCol) {
  const tile = prev.tiles.find((t) => t.id === tileId);
  if (!tile) return { state: prev, result: "invalid" };
  if (tile.row === destRow && tile.col === destCol) {
    return { state: prev, result: "invalid" };
  }
  const grid = gridOf(prev.tiles);
  const dst = grid[destRow][destCol];

  // Different-value occupied destination → invalid.
  if (dst && dst.value !== tile.value) {
    return { state: prev, result: "invalid" };
  }

  const next = clone(prev);
  clearFlags(next);
  const srcRef = next.tiles.find((t) => t.id === tile.id);

  if (dst) {
    // MERGE.
    const dstRef = next.tiles.find((t) => t.id === dst.id);
    dstRef.value *= 2;
    dstRef.justMerged = true;
    if (dstRef.value > next.best) next.best = dstRef.value;
    // Source slides into dest position before being removed (visual cue).
    srcRef.row = destRow;
    srcRef.col = destCol;
    srcRef.absorbed = true;
    next.score += dstRef.value;
    next.moves++;
    if (!next.won && dstRef.value >= WIN_VALUE) next.won = true;
    // Spawn a new tile after a merge (only if there's room).
    spawnTile(next);
    // Now actually remove absorbed tile.
    next.tiles = next.tiles.filter((t) => !t.absorbed);
    return {
      state: next,
      result: "merge",
      mergedValue: dstRef.value,
      scoreGained: dstRef.value,
    };
  }

  // RELOCATE — empty destination cell.
  srcRef.row = destRow;
  srcRef.col = destCol;
  next.moves++;
  spawnTile(next);
  return { state: next, result: "move", scoreGained: 0 };
}

// Is the board completely full with no possible merges? (= game over)
export function isGameOver(state) {
  if (state.tiles.length < SIZE * SIZE) return false;
  // Board full — game over only if no two tiles share a value anywhere.
  const seen = new Set();
  for (const t of state.tiles) {
    if (seen.has(t.value)) return false;
    seen.add(t.value);
  }
  return true;
}
